import { api } from "$lib/api/client";

export interface Category {
  id: string;
  workspace_id: string;
  name: string;
  kind: "income" | "expense";
  parent_id?: string | null;
  color?: string | null;
  icon?: string | null;
}

const DEFAULTS: Array<Pick<Category, "name" | "kind" | "color" | "icon">> = [
  { name: "Alimentação", kind: "expense", color: "#f97316", icon: "🍔" },
  { name: "Transporte", kind: "expense", color: "#3b82f6", icon: "🚗" },
  { name: "Moradia", kind: "expense", color: "#8b5cf6", icon: "🏠" },
  { name: "Saúde", kind: "expense", color: "#ef4444", icon: "💊" },
  { name: "Lazer", kind: "expense", color: "#ec4899", icon: "🎮" },
  { name: "Educação", kind: "expense", color: "#06b6d4", icon: "📚" },
  { name: "Outras despesas", kind: "expense", color: "#64748b", icon: "💸" },
  { name: "Salário", kind: "income", color: "#10b981", icon: "💰" },
  { name: "Freelance", kind: "income", color: "#22c55e", icon: "🧾" },
  { name: "Outras receitas", kind: "income", color: "#84cc16", icon: "✨" },
];

function createCategories() {
  let list = $state<Category[]>([]);
  let loading = $state(false);
  let loaded = $state(false);

  async function load(force = false) {
    if (loaded && !force) return;
    loading = true;
    try { list = await api<Category[]>("/categories"); loaded = true; }
    finally { loading = false; }
  }

  async function ensureSeeded(): Promise<Category[]> {
    await load();
    if (list.length > 0) return list;
    const created: Category[] = [];
    for (const d of DEFAULTS) {
      try {
        const c = await api<Category>("/categories", { method: "POST", body: JSON.stringify(d) });
        created.push(c);
      } catch { /* ignore */ }
    }
    list = [...list, ...created];
    return list;
  }

  async function create(payload: Partial<Category> & { name: string; kind: "income" | "expense" }) {
    const c = await api<Category>("/categories", { method: "POST", body: JSON.stringify(payload) });
    list = [...list, c];
    return c;
  }

  async function update(id: string, payload: Partial<Category>) {
    const c = await api<Category>(`/categories/${id}`, { method: "PATCH", body: JSON.stringify(payload) });
    list = list.map((x) => (x.id === id ? c : x));
    return c;
  }

  async function remove(id: string) {
    await api(`/categories/${id}`, { method: "DELETE" });
    list = list.filter((x) => x.id !== id);
  }

  function byKind(kind: "income" | "expense"): Category[] {
    return list.filter((c) => c.kind === kind);
  }

  function byId(id: string | null | undefined): Category | undefined {
    if (!id) return undefined;
    return list.find((c) => c.id === id);
  }

  return {
    get list() { return list; },
    get loading() { return loading; },
    load, ensureSeeded, create, update, remove, byKind, byId,
  };
}

export const categories = createCategories();
